import React from 'react';
import { Box, Card, Skeleton } from '@mui/material';

const TableSkeleton = () => (
  <Card sx={{ borderRadius: '25px', padding: '24px', backgroundColor: '#fff' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Skeleton variant="text" width={200} height={34} animation="wave" />
      <Skeleton variant="rounded" width={180} height={44} animation="wave" sx={{ borderRadius: '29px' }} />
    </Box>
    <Box sx={{ display: 'flex', gap: 2, mb: 2, px: 1 }}>
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} variant="text" sx={{ flex: 1 }} height={22} animation="wave" />
      ))}
    </Box>
    {[...Array(8)].map((_, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center', px: 1 }}>
        {[...Array(6)].map((_, j) => (
          <Skeleton key={j} variant="rounded" height={44} animation="wave" sx={{ borderRadius: '8px', flex: 1 }} />
        ))}
      </Box>
    ))}
  </Card>
);

const CardListSkeleton = () => (
  <Card sx={{ borderRadius: '25px', padding: '24px', backgroundColor: '#fff' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Skeleton variant="text" width={200} height={34} animation="wave" />
      <Skeleton variant="rounded" width={160} height={44} animation="wave" sx={{ borderRadius: '29px' }} />
    </Box>
    {[...Array(5)].map((_, i) => (
      <Box
        key={i}
        sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', p: 1.5, borderRadius: '16px', border: '1px solid #f0f0f0' }}
      >
        <Skeleton variant="rounded" width={100} height={80} animation="wave" sx={{ borderRadius: '12px', flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="55%" height={24} animation="wave" sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="35%" height={18} animation="wave" sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="80%" height={18} animation="wave" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Skeleton variant="circular" width={32} height={32} animation="wave" />
          <Skeleton variant="circular" width={32} height={32} animation="wave" />
          <Skeleton variant="circular" width={32} height={32} animation="wave" />
        </Box>
      </Box>
    ))}
  </Card>
);

const GridSkeleton = () => (
  <Card sx={{ borderRadius: '25px', padding: '24px', backgroundColor: '#fff' }}>
    <Skeleton variant="text" width={200} height={34} animation="wave" sx={{ mb: 3 }} />
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
      {[...Array(6)].map((_, i) => (
        <Box key={i} sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #f0f0f0', p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Skeleton variant="circular" width={32} height={32} animation="wave" sx={{ flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={18} animation="wave" />
              <Skeleton variant="text" width="40%" height={14} animation="wave" />
            </Box>
          </Box>
          <Skeleton variant="rounded" width="100%" height={160} animation="wave" sx={{ borderRadius: '12px', mb: 1 }} />
          <Skeleton variant="text" width="90%" height={18} animation="wave" />
          <Skeleton variant="text" width="55%" height={18} animation="wave" />
        </Box>
      ))}
    </Box>
  </Card>
);

const TicketSkeleton = () => (
  <Box sx={{ p: 1 }}>
    {[...Array(6)].map((_, i) => (
      <Box
        key={i}
        sx={{ display: 'flex', gap: 2, mb: 2, p: 2, borderRadius: '16px', background: '#fff', boxShadow: '0px 1px 4px rgba(0,0,0,0.06)' }}
      >
        <Skeleton variant="circular" width={48} height={48} animation="wave" sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Skeleton variant="text" width={130} height={22} animation="wave" />
            <Skeleton variant="text" width={80} height={18} animation="wave" />
          </Box>
          <Skeleton variant="text" width="85%" height={18} animation="wave" />
          <Skeleton variant="text" width="50%" height={18} animation="wave" />
        </Box>
      </Box>
    ))}
  </Box>
);

const FormSkeleton = () => (
  <Box sx={{ p: 1 }}>
    <Skeleton variant="text" width={180} height={32} animation="wave" sx={{ mb: 3 }} />
    <Skeleton variant="rounded" height={52} animation="wave" sx={{ borderRadius: '12px', mb: 2 }} />
    <Skeleton variant="rounded" height={52} animation="wave" sx={{ borderRadius: '12px', mb: 3 }} />
    <Skeleton variant="rounded" height={52} animation="wave" sx={{ borderRadius: '29px' }} />
  </Box>
);

const NotificationSkeleton = () => (
  <Box>
    {[...Array(3)].map((_, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: '10px 16px' }}>
        <Skeleton variant="circular" width={40} height={40} animation="wave" sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="70%" height={18} animation="wave" />
          <Skeleton variant="text" width="45%" height={14} animation="wave" />
        </Box>
      </Box>
    ))}
  </Box>
);

const VARIANTS = {
  table: TableSkeleton,
  cardList: CardListSkeleton,
  grid: GridSkeleton,
  ticket: TicketSkeleton,
  form: FormSkeleton,
  notification: NotificationSkeleton,
};

const Loader = ({ variant = 'table' }) => {
  const SkeletonComponent = VARIANTS[variant] || TableSkeleton;
  return <SkeletonComponent />;
};

export default Loader;
